import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Check, ChevronDown, Pencil, Plus, Trash2, X } from 'lucide-react';

import { ALL_DEVICES_ID } from '../services/groupsService';

// Group selector shown next to the Active/Archive toggle. Besides picking the current
// group it also hosts group management (create / rename / delete). "All Devices" is a
// fixed, non-editable entry that always sits at the top.
function GroupDropdown({
    groups = [],
    selectedGroupId,
    onSelect,
    onCreate,
    onRename,
    onDelete,
    disabled = false,
}: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'RealTime.Groups' });
    const rootRef = useRef<HTMLDivElement>(null);

    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [draftName, setDraftName] = useState('');
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);
    // Edit mode shows the rename/delete controls on every row at once. An explicit toggle
    // rather than reveal-on-hover: the controls stay put while you work down the list, and
    // they're reachable on touch devices, which have no hover at all.
    const [editMode, setEditMode] = useState(false);

    const allDevicesLabel = t('allDevices');
    const selectedGroup = groups.find((g: any) => g.id === selectedGroupId);
    // Vendor/admin accounts see groups from multiple companies; prefix those with the
    // company name so same-named groups are distinguishable ("Company Name - Group").
    const multiCompany = new Set(groups.map((g: any) => g.company_id).filter(Boolean)).size > 1;
    const groupLabel = (g: any) => (multiCompany && g.company_name ? `${g.company_name} - ${g.name}` : g.name);
    const selectedLabel =
        selectedGroupId === ALL_DEVICES_ID
            ? allDevicesLabel
            : selectedGroup
              ? groupLabel(selectedGroup)
              : allDevicesLabel;

    // Close the popover (and reset any in-progress editing) on outside click.
    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                closeMenu();
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    // Close the menu if the control becomes locked (e.g. switching to the Archive tab).
    useEffect(() => {
        if (disabled) closeMenu();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled]);

    const closeMenu = () => {
        setOpen(false);
        setEditingId(null);
        setCreating(false);
        setNewName('');
        setConfirmingDeleteId(null);
        setEditMode(false);
    };

    const select = (id: string) => {
        onSelect(id);
        closeMenu();
    };

    const beginRename = (group: any) => {
        setConfirmingDeleteId(null);
        setEditingId(group.id);
        setDraftName(group.name);
    };

    const commitRename = () => {
        const name = draftName.trim();
        if (name && name !== groups.find((g: any) => g.id === editingId)?.name) {
            onRename(editingId, name);
        }
        setEditingId(null);
    };

    const commitCreate = () => {
        const name = newName.trim();
        if (name) {
            onCreate(name);
        }
        setCreating(false);
        setNewName('');
    };

    return (
        <div ref={rootRef} className='relative w-full md:w-auto'>
            <button
                type='button'
                onClick={() => (open ? closeMenu() : setOpen(true))}
                disabled={disabled}
                className='flex w-full items-center justify-between gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 disabled:hover:border-slate-200 md:w-56'
            >
                <span className='truncate'>{selectedLabel}</span>
                <ChevronDown
                    size={16}
                    className={`flex-shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
                />
            </button>

            {open && (
                <div className='absolute left-0 z-30 mt-2 w-full min-w-[16rem] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl md:w-72'>
                    <ul className='max-h-72 overflow-y-auto py-1'>
                        {/* All Devices — fixed, non-editable */}
                        <li>
                            <button
                                type='button'
                                onClick={() => select(ALL_DEVICES_ID)}
                                className='flex w-full items-center justify-between px-4 py-2.5 text-left text-sm font-semibold text-slate-700 transition hover:bg-slate-50'
                            >
                                <span className='truncate'>{allDevicesLabel}</span>
                                {selectedGroupId === ALL_DEVICES_ID && (
                                    <Check size={16} className='flex-shrink-0 text-blue-500' />
                                )}
                            </button>
                        </li>

                        {groups.length > 0 && <li className='mx-4 my-1 border-t border-slate-100' />}

                        {groups.map((group: any) => (
                            <li key={group.id}>
                                {editingId === group.id ? (
                                    <div className='flex items-center gap-2 px-3 py-2'>
                                        {/* This input only renders once the user picks "rename", so moving
                                            focus into it continues their action rather than hijacking focus
                                            on page load, which is what no-autofocus guards against. */}
                                        {/* eslint-disable jsx-a11y/no-autofocus */}
                                        <input
                                            autoFocus
                                            value={draftName}
                                            onFocus={(e) => e.target.select()}
                                            onChange={(e) => setDraftName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') commitRename();
                                                else if (e.key === 'Escape') setEditingId(null);
                                            }}
                                            className='min-w-0 flex-1 rounded-lg border border-blue-300 px-2 py-1 text-sm text-slate-700 focus:border-blue-400 focus:outline-none'
                                        />
                                        {/* eslint-enable jsx-a11y/no-autofocus */}
                                        <button
                                            type='button'
                                            onClick={commitRename}
                                            aria-label={t('save')}
                                            className='text-blue-500 hover:text-blue-600'
                                        >
                                            <Check size={16} />
                                        </button>
                                        <button
                                            type='button'
                                            onClick={() => setEditingId(null)}
                                            aria-label={t('cancel')}
                                            className='text-slate-400 hover:text-slate-600'
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ) : confirmingDeleteId === group.id ? (
                                    <div className='flex items-center justify-between gap-2 bg-red-50/60 px-4 py-2'>
                                        <span className='truncate text-sm text-slate-600'>{t('deleteConfirm')}</span>
                                        <span className='flex flex-shrink-0 gap-2'>
                                            <button
                                                type='button'
                                                onClick={() => {
                                                    onDelete(group.id);
                                                    setConfirmingDeleteId(null);
                                                }}
                                                className='rounded-md px-2 py-0.5 text-xs font-semibold text-red-600 hover:bg-red-100'
                                            >
                                                {t('delete')}
                                            </button>
                                            <button
                                                type='button'
                                                onClick={() => setConfirmingDeleteId(null)}
                                                className='rounded-md px-2 py-0.5 text-xs font-semibold text-slate-500 hover:bg-slate-100'
                                            >
                                                {t('cancel')}
                                            </button>
                                        </span>
                                    </div>
                                ) : (
                                    <div className='flex items-center px-1'>
                                        <button
                                            type='button'
                                            onClick={() => select(group.id)}
                                            className='flex min-w-0 flex-1 items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50'
                                        >
                                            <span className='truncate'>{groupLabel(group)}</span>
                                            {selectedGroupId === group.id && (
                                                <Check size={16} className='flex-shrink-0 text-blue-500' />
                                            )}
                                        </button>
                                        {/* Rename/delete are mounted only in edit mode — no hover
                                            involved, so they can't be left behind on the wrong row. */}
                                        {editMode && (
                                            <span className='flex flex-shrink-0 items-center gap-1 pr-2'>
                                                <button
                                                    type='button'
                                                    onClick={() => beginRename(group)}
                                                    aria-label={t('rename')}
                                                    className='rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                                                >
                                                    <Pencil size={14} />
                                                </button>
                                                <button
                                                    type='button'
                                                    onClick={() => {
                                                        setEditingId(null);
                                                        setConfirmingDeleteId(group.id);
                                                    }}
                                                    aria-label={t('delete')}
                                                    className='rounded-md p-1 text-slate-400 hover:bg-red-50 hover:text-red-500'
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </span>
                                        )}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>

                    <div className='border-t border-slate-100 p-2'>
                        {creating ? (
                            <div className='flex items-center gap-2 px-1'>
                                {/* Same as the rename input above: this only renders after the user
                                    clicks "new group", so the focus move is a continuation of their action. */}
                                {/* eslint-disable jsx-a11y/no-autofocus */}
                                <input
                                    autoFocus
                                    value={newName}
                                    placeholder={t('groupNamePlaceholder')}
                                    onChange={(e) => setNewName(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') commitCreate();
                                        else if (e.key === 'Escape') {
                                            setCreating(false);
                                            setNewName('');
                                        }
                                    }}
                                    className='min-w-0 flex-1 rounded-lg border border-blue-300 px-2 py-1.5 text-sm text-slate-700 focus:border-blue-400 focus:outline-none'
                                />
                                {/* eslint-enable jsx-a11y/no-autofocus */}
                                <button
                                    type='button'
                                    onClick={commitCreate}
                                    aria-label={t('save')}
                                    className='rounded-lg bg-blue-500 p-1.5 text-white hover:bg-blue-600'
                                >
                                    <Check size={16} />
                                </button>
                            </div>
                        ) : (
                            <div className='flex items-center gap-1'>
                                <button
                                    type='button'
                                    onClick={() => setCreating(true)}
                                    className='flex min-w-0 flex-1 items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-blue-500 transition hover:bg-blue-50'
                                >
                                    <Plus size={16} />
                                    <span className='truncate'>{t('newGroup')}</span>
                                </button>
                                {/* Toggles the per-row rename/delete controls for the whole list.
                                    Leaving edit mode also drops any in-progress rename/confirm. */}
                                {groups.length > 0 && (
                                    <button
                                        type='button'
                                        aria-pressed={editMode}
                                        onClick={() => {
                                            setEditMode((on: boolean) => !on);
                                            setEditingId(null);
                                            setConfirmingDeleteId(null);
                                        }}
                                        className={`flex flex-shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition ${
                                            editMode
                                                ? 'bg-blue-50 text-blue-600'
                                                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                                        }`}
                                    >
                                        <Pencil size={14} />
                                        {editMode ? t('done') : t('edit')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default GroupDropdown;
